// src/page/CouponNew.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import Select from 'react-select';

export default function CouponNew() {
  useAuthGuard();
  const navigate = useNavigate();

  // ---------- メッセージ（日本語固定） ----------
  const MSG = {
    errPackageName: 'クーポン名は必須です',
    errSpot: 'スポットは必須です',
    errPublish: '公開設定を選択してください',
    errPublishRange: '公開期間は必須です',
    errSales: '有効期間は必須です',
    errCouponLevel: 'クーポンレベルを選択してください',
    errVoucherUsage: 'チケット利用区分を選択してください',
    errNumberOfUsePerUser: '1人利用可能回数は0以上の数値で必須です',
    errUseLimitByDay: '1日の使用上限回数は0以上の数値で必須です',
    saved: '登録しました',
    apiError: 'サーバーエラーが発生しました'
  };

  // ---------- helpers ----------
  const pad = (n) => String(n).padStart(2, '0');
  const fmtDate = (d) =>
    `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const fmtRange = (a, b) => (a && b ? `${fmtDate(a)} ~ ${fmtDate(b)}` : '');

  // "YYYY/MM/DD HH:mm" -> ISO (UTC, RFC3339)
  const parseLocalToISO = (s) => {
    if (!s) return null;
    const m = s.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})\s+(\d{1,2}):(\d{2})$/);
    if (!m) return null;
    const [, yStr, moStr, dStr, hStr, miStr] = m;
    const y = Number(yStr), mo = Number(moStr), d = Number(dStr), h = Number(hStr), mi = Number(miStr);
    const dt = new Date(y, mo - 1, d, h, mi, 0);
    return dt.toISOString();
  };

  const parseRangeToISO = (rangeText) => {
    if (!rangeText) return { start: null, end: null };
    const [a, b] = rangeText.split(/~|〜|から|-/).map((s) => s.trim());
    return { start: parseLocalToISO(a), end: parseLocalToISO(b) };
  };

  // ---------- states ----------
  const [packageName, setCouponName] = useState('');
  const [spotId, setSpotId] = useState('');
  const [spots, setSpots] = useState([]);
  const [loadingSpots, setLoadingSpots] = useState(false);

  const spotOptions = useMemo(
    () => spots.map(s => ({ value: s.spotId, label: s.spotName })),
    [spots]
  );

  const [displayOrder, setDisplayOrder] = useState('');
  const [publishedFlag, setPublishedFlag] = useState(null); // true/false/null

  const [howToUse, setHowToUse] = useState('');
  const [termsOfUse, setTermsOfUse] = useState('');
  const [caution, setCaution] = useState('');

  const [salesRange, setSalesRange] = useState('');
  const [publishRange, setPublishRange] = useState('');

  const [couponLevel, setCouponLevel] = useState(''); // radios 0-3
  const [usePoint, setUsePoint] = useState('');
  const [maxNumber, setMaxNumber] = useState('');
  const [maxPerUser, setMaxPerUser] = useState(''); // voucher: numberOfUsePerUser
  const [useLimitByDay, setUseLimitByDay] = useState(''); // voucher
  const [voucherUsage, setVoucherUsage] = useState(''); // '0' | '1'

  const [loading, setLoading] = useState(false); // 新規は詳細取得なし
  const [apiError, setApiError] = useState('');
  const [errors, setErrors] = useState({});

  // pickers
  const salesPickerRef = useRef(null);
  const publishPickerRef = useRef(null);

  // input refs
  const salesInputRef = useRef(null);
  const publishInputRef = useRef(null);

  // submit / upload states
  const [submitting, setSubmitting] = useState(false);
  const [deleting] = useState(false); // 構造維持のため存在させる（新規では未使用）

  // ---- 画像アップロード（単枚） ----
  const [imageFile, setImageFile] = useState(null);      // File | null
  const [imagePreview, setImagePreview] = useState('');  // blob URL
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const setSingleImage = (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      alert('画像ファイルを選択してください');
      return;
    }
    if (imagePreview && imagePreview.startsWith('blob:')) {
      try { URL.revokeObjectURL(imagePreview); } catch { }
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };
  const handlePickClick = () => fileInputRef.current?.click();
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setSingleImage(file);
    e.target.value = '';
  };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) setSingleImage(file);
  };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); };
  const clearImage = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      try { URL.revokeObjectURL(imagePreview); } catch { }
    }
    setImageFile(null);
    setImagePreview('');
  };
  useEffect(() => () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      try { URL.revokeObjectURL(imagePreview); } catch { }
    }
  }, [imagePreview]);

  // ---- 新規作成後の画像アップロード (/v1/file -> PUT /v1/coupon/:id) ----
  const uploadImageForNew = async (newId) => {
    if (!imageFile) return null;
    setUploading(true);
    try {
      const token = localStorage.getItem('access_token');
      const fd = new FormData();
      fd.append('file', imageFile, imageFile.name);
      fd.append('path', `coupon/${newId}/`);
      const res = await fetch(`${API_BASE_URL}/v1/file`, {
        method: 'POST',
        headers: { Authorization: token ? `Bearer ${token}` : '' },
        body: fd
      });
      if (!res.ok) {
        let msg = `画像アップロードに失敗しました (HTTP ${res.status})`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
        throw new Error(msg);
      }
      try {
        const j = await res.json();
        return j?.path || j?.result || null;
      } catch {
        return await res.text();
      }
    } finally {
      setUploading(false);
    }
  };

  const savingOrUploading = submitting || uploading;

  // --- flatpickr init ---
  useEffect(() => {
    if (!window.flatpickr) return;
    const ja = window.flatpickr.l10ns?.ja || {};

    const salesInput = document.getElementById('sales_range');
    if (salesInput) {
      salesPickerRef.current = window.flatpickr(salesInput, {
        mode: 'range',
        enableTime: true,
        time_24hr: true,
        dateFormat: 'Y/m/d H:i',
        locale: { ...ja, rangeSeparator: ' ~ ' },
        onChange: (selectedDates) => {
          if (selectedDates?.length === 2) {
            setSalesRange(`${fmtDate(selectedDates[0])} ~ ${fmtDate(selectedDates[1])}`);
          } else if (!selectedDates?.length) {
            setSalesRange('');
          }
        }
      });
    }

    const publishInput = document.getElementById('publish_range');
    if (publishInput) {
      publishPickerRef.current = window.flatpickr(publishInput, {
        mode: 'range',
        enableTime: true,
        time_24hr: true,
        dateFormat: 'Y/m/d H:i',
        locale: { ...ja, rangeSeparator: ' ~ ' },
        onChange: (selectedDates) => {
          if (selectedDates?.length === 2) {
            setPublishRange(`${fmtDate(selectedDates[0])} ~ ${fmtDate(selectedDates[1])}`);
          } else if (!selectedDates?.length) {
            setPublishRange('');
          }
        }
      });
    }

    return () => {
      try { salesPickerRef.current?.destroy(); } catch { }
      try { publishPickerRef.current?.destroy(); } catch { }
    };
  }, []);

  // reflect controlled -> picker (and clear when empty)
  useEffect(() => {
    if (!salesPickerRef.current) return;
    if (salesRange && salesRange.includes('~')) {
      const [a, b] = salesRange.split('~').map(s => s.trim());
      salesPickerRef.current.setDate([a, b], true);
    } else {
      try { salesPickerRef.current.clear(); } catch { }
    }
  }, [salesRange]);

  useEffect(() => {
    if (!publishPickerRef.current) return;
    if (publishRange && publishRange.includes('~')) {
      const [a, b] = publishRange.split('~').map(s => s.trim());
      publishPickerRef.current.setDate([a, b], true);
    } else {
      try { publishPickerRef.current.clear(); } catch { }
    }
  }, [publishRange]);

  // clear buttons
  const clearSales = () => { setSalesRange(''); try { salesPickerRef.current?.clear(); } catch { } };
  const clearPublish = () => { setPublishRange(''); try { publishPickerRef.current?.clear(); } catch { } };

  // spots
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoadingSpots(true);
        const token = localStorage.getItem('access_token');
        const res = await fetch(`${API_BASE_URL}/v1/spot?page=1&size=200`, {
          headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
          signal: ac.signal
        });
        const data = await res.json().catch(() => ({}));
        const list = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
        const mapped = list.map(s => ({
          spotId: s.spotId ?? s.id ?? s.spot_id ?? '',
          spotName: s.spotName ?? s.name ?? s.spot_name ?? ''
        })).filter(x => x.spotId && x.spotName);
        setSpots(mapped);
      } catch {
        setSpots([]);
      } finally {
        setLoadingSpots(false);
      }
    })();
    return () => ac.abort();
  }, []);

  // Submit (validate + POST)
  const handleSubmit = async (e) => {
    e.preventDefault();

    const salesVal = salesRange?.trim() ?? '';
    const publishVal = publishRange?.trim() ?? '';

    const newErrors = {};
    if (!packageName.trim()) newErrors.packageName = MSG.errPackageName;
    if (!spotId) newErrors.spotId = MSG.errSpot;
    if (publishedFlag !== true && publishedFlag !== false) newErrors.publishedFlag = MSG.errPublish;

    if (!salesVal) newErrors.salesRange = MSG.errSales;
    if (!publishVal) newErrors.publishRange = MSG.errPublishRange;
    if (couponLevel === '') newErrors.couponLevel = MSG.errCouponLevel;
    if (voucherUsage !== '0' && voucherUsage !== '1') {
      newErrors.voucherUsage = MSG.errVoucherUsage;
    }
    const np = Number(maxPerUser);
    if (!Number.isInteger(np) || np < 0) {
      newErrors.numberOfUsePerUser = MSG.errNumberOfUsePerUser;
    }
    const ulbdNum = Number(useLimitByDay);
    if (!Number.isInteger(ulbdNum) || ulbdNum < 0) {
      newErrors.useLimitByDay = MSG.errUseLimitByDay;
    }
    setErrors(newErrors);
    const keys = Object.keys(newErrors);
    if (keys.length > 0) {
      const firstKey = keys[0];
      const node = document.querySelector(`[data-error-key="${firstKey}"]`);
      if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const token = localStorage.getItem('access_token') || '';
    const { start: salesStartDate, end: salesEndDate } = parseRangeToISO(salesVal);
    const { start: publishedStartDate, end: publishedEndDate } = parseRangeToISO(publishVal);

    // ==== ここが変更点：coupon と voucher に分割 ====
    const payload = {
      coupon: {
        packageName: packageName || '',
        spotId: spotId || '',
        displayOrder: displayOrder === '' ? null : Number(displayOrder),
        publishedFlag: !!publishedFlag,

        salesStartDate,
        salesEndDate,
        publishedStartDate,
        publishedEndDate,

        howToUse: howToUse || '',
        termsOfUse: termsOfUse || '',
        caution: caution || '',

        couponArea: null, // 画面に無いので null
        couponGenre: null, // 画面に無いので null
        couponLevel: couponLevel === '' ? null : Number(couponLevel),
        usePoint: usePoint === '' ? null : Number(usePoint),
        maxNumber: maxNumber === '' ? null : Number(maxNumber),
        sellingPrice: null, // 未使用なら null
        recommendAttributes: null // 未使用なら null
      },
      voucher: {
        voucherUsage, // '0' | '1'
        numberOfUsePerUser: Number(maxPerUser),
        useLimitByDay: Number(useLimitByDay)
      }
    };

    try {
      setSubmitting(true);
      setApiError('');
      const res = await fetch(`${API_BASE_URL}/v1/coupon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `HTTP ${res.status}`);
      }

      // 作成レスポンスからIDを取得
      let newPackageId = null;
      try {
        const created = await res.json();
        newPackageId = created?.coupon?.packageId ?? created?.packageId ?? created?.couponId ?? null;
      } catch {
        // 空レスポンスの場合は newPackageId 取得不可
      }

      // 画像があればアップロード＆PUTで紐付け（パッケージ側）
      if (imageFile && newPackageId) {
        const uploadedImagePath = await uploadImageForNew(newPackageId);
        if (!uploadedImagePath) throw new Error('画像のアップロードに失敗しました。');

        const putPayload = {
          ...payload.coupon,
          couponImage: uploadedImagePath,
          imagePath: uploadedImagePath
        };
        const token2 = localStorage.getItem('access_token');
        const res2 = await fetch(`${API_BASE_URL}/v1/coupon/${newPackageId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(token2 ? { Authorization: `Bearer ${token2}` } : {})
          },
          body: JSON.stringify({ coupon: putPayload })
        });
        if (!res2.ok) {
          let msg = `画像付き更新に失敗しました (HTTP ${res2.status})`;
          try { const j2 = await res2.json(); if (j2?.error) msg = j2.error; } catch { }
          throw new Error(msg);
        }
      }

      alert(MSG.saved);
      navigate('/coupon');
    } catch (err) {
      console.error(err);
      setApiError(MSG.apiError);
    } finally {
      setSubmitting(false);
    }
  };

  // class helpers
  const invalid = (key) => (errors[key] ? 'is-invalid' : '');
  const feedback = (key) => (errors[key] ? <div className="invalid-feedback d-block">{errors[key]}</div> : null);

  return (
    <Layout>
      <>
        <nav className="mb-3" aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item"><a href="/coupon">クーポン管理</a></li>
            <li className="breadcrumb-item active">新規登録</li>
          </ol>
        </nav>

        {apiError && <div className="alert alert-danger">{apiError}</div>}

        <form className="mb-9" onSubmit={handleSubmit} noValidate>
          <div className="row g-3 flex-between-end mb-5">
            <div className="col-auto">
              <h2 className="mb-2">クーポン新規登録</h2>
            </div>
            <div className="col-auto">
              <button
                className="btn btn-phoenix-secondary me-2 mb-2 mb-sm-0"
                type="button"
                onClick={() => window.history.back()}
                disabled={savingOrUploading || deleting}
              >
                キャンセル
              </button>
              <button
                className="btn btn-primary me-2 mb-2 mb-sm-0"
                type="submit"
                disabled={savingOrUploading || deleting}
              >
                {uploading ? '画像アップロード中…' : (submitting ? '登録中…' : '登録')}
              </button>
            </div>
          </div>

          <div className="row g-5">
            <div className="col-12 col-xl-8">

              <div className="mb-4" data-error-key="packageName">
                <h4 className="mb-3">クーポン名<span className="text-danger">*</span></h4>
                <input
                  className={`form-control ${invalid('packageName')}`}
                  type="text"
                  name="package_name"
                  value={packageName}
                  onChange={(e) => setCouponName(e.target.value)}
                  required
                />
                {feedback('packageName')}
              </div>

              <div className="mb-4" data-error-key="spotId">
                <h4 className="mb-3">スポット名<span className="text-danger">*</span></h4>
                <Select
                  options={spotOptions}
                  value={spotOptions.find(o => o.value === spotId) || null}
                  onChange={(opt) => setSpotId(opt?.value || '')}
                  isDisabled={loadingSpots || submitting}
                  isClearable
                  placeholder={loadingSpots ? '読み込み中…' : '選択または検索'}
                  classNamePrefix="react-select"
                  className="w-100"
                  menuPortalTarget={document.body}
                  styles={{
                    container: (base) => ({ ...base, width: '100%', flex: 1, minWidth: 0 }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  filterOption={(opt, input) =>
                    opt.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
                {feedback('spotId')}
              </div>

              {/* テキスト */}
              <div className="mb-4">
                <h4 className="mb-3">使用方法</h4>
                <textarea className="form-control" name="how_to_use" rows={4} value={howToUse} onChange={(e) => setHowToUse(e.target.value)} />
              </div>
              <div className="mb-4">
                <h4 className="mb-3">利用条件</h4>
                <textarea className="form-control" name="terms_of_use" rows={4} value={termsOfUse} onChange={(e) => setTermsOfUse(e.target.value)} />
              </div>
              <div className="mb-4">
                <h4 className="mb-3">注意事項</h4>
                <textarea className="form-control" name="caution" rows={4} value={caution} onChange={(e) => setCaution(e.target.value)} />
              </div>

              <div className="row">
                <div className="mb-4 col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">必要ポイント</h4>
                    <input className="form-control" type="number" name="use_point" value={usePoint ?? ''} onChange={(e) => setUsePoint(e.target.value)} />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">発行枚数</h4>
                    <input className="form-control" type="number" name="max_number" value={maxNumber ?? ''} onChange={(e) => setMaxNumber(e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className={`mb-4 ${invalid('couponLevel')}`} data-error-key="couponLevel">
                    <h4 className="mb-3">クーポンレベル<span className="text-danger">*</span></h4>
                    <div className="d-flex align-items-center gap-3">
                      {['0', '1', '2', '3'].map((lv) => (
                        <label className="mb-0" key={lv}>
                          <input
                            type="radio"
                            name="coupon_level"
                            value={lv}
                            checked={couponLevel === lv}
                            onChange={(e) => setCouponLevel(e.target.value)}
                          /> {lv}
                        </label>
                      ))}
                    </div>
                    {feedback('couponLevel')}
                  </div>
                </div>
                <div className="col-md-6" data-error-key="numberOfUsePerUser">
                  <div className="mb-4">
                    <h4 className="mb-3">1人利用可能回数</h4>
                    <input
                      className={`form-control ${invalid('numberOfUsePerUser')}`}
                      type="number"
                      name="number_of_use_per_user"
                      min={0}
                      step={1}
                      value={maxPerUser ?? ''}
                      onChange={(e) => setMaxPerUser(e.target.value)}
                      required
                    />
                    {feedback('numberOfUsePerUser')}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className={`mb-4 ${invalid('voucherUsage')}`} data-error-key="voucherUsage">
                    <h4 className="mb-3">チケット利用区分<span className="text-danger">*</span></h4>
                    <div className="d-flex align-items-center gap-3">
                      <label className="mb-0">
                        <input
                          type="radio"
                          name="voucher_usage"
                          value="0"
                          checked={voucherUsage === '0'}
                          onChange={(e) => setVoucherUsage(e.target.value)}
                          required
                        /> 目視確認
                      </label>
                      <label className="mb-0">
                        <input
                          type="radio"
                          name="voucher_usage"
                          value="1"
                          checked={voucherUsage === '1'}
                          onChange={(e) => setVoucherUsage(e.target.value)}
                          required
                        /> QR表示
                      </label>
                    </div>
                    {feedback('voucherUsage')}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className={`mb-4 ${invalid('useLimitByDay')}`} data-error-key="useLimitByDay">
                    <h4 className="mb-3">1日の使用上限回数</h4>
                    <input
                      className={`form-control ${invalid('useLimitByDay')}`}
                      type="number"
                      name="use_limit_by_day"
                      min={0}
                      step={1}
                      value={useLimitByDay ?? ''}
                      onChange={(e) => setUseLimitByDay(e.target.value)}
                      required
                    />
                    {feedback('useLimitByDay')}
                  </div>
                </div>
              </div>

            </div>

            <div className="col-12 col-xl-4">
              <div className="row g-2">
                <div className="col-12 col-sm-6 col-xl-12" data-error-key="publishedFlag">
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="mb-4">
                        <h5 className="mb-3">公開設定<span className="text-danger">*</span></h5>
                        <div className="d-flex align-items-center gap-3">
                          <label className="mb-0">
                            <input
                              type="radio"
                              name="publish_status"
                              value="0"
                              checked={publishedFlag === false}
                              onChange={() => setPublishedFlag(false)}
                              required
                            /> 非公開
                          </label>
                          <label className="mb-0">
                            <input
                              type="radio"
                              name="publish_status"
                              value="1"
                              checked={publishedFlag === true}
                              onChange={() => setPublishedFlag(true)}
                              required
                            /> 公開
                          </label>
                        </div>
                        {feedback('publishedFlag')}
                      </div>

                      {/* 公開期間 */}
                      <div className="mb-4" data-error-key="publishRange">
                        <div className="d-flex flex-wrap mb-2">
                          <h5 className="mb-0 text-body-highlight me-2">公開期間<span className="text-danger">*</span></h5>
                        </div>
                        <div className="input-group">
                          <input
                            id="publish_range"
                            className={`form-control ${invalid('publishRange')}`}
                            type="text"
                            name="publish_range"
                            placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                            value={publishRange}
                            ref={publishInputRef}
                            required
                            readOnly
                            onChange={(e) => setPublishRange(e.target.value)}
                          />
                          <span
                            className="input-group-text"
                            role="button"
                            tabIndex={0}
                            onClick={clearPublish}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clearPublish(); }}
                            aria-label="クリア"
                            title="クリア"
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                          >×</span>
                        </div>
                        {feedback('publishRange')}
                      </div>

                      {/* 有効期間（画面右に残す場合） */}
                      <div className="mb-4" data-error-key="salesRange">
                        <div className="d-flex flex-wrap mb-2">
                          <h5 className="mb-0 text-body-highlight me-2">
                            有効期間<span className="text-danger">*</span>
                          </h5>
                        </div>
                        <div className="input-group">
                          <input
                            id="sales_range"
                            className={`form-control ${invalid('salesRange')}`}
                            type="text"
                            name="sales_range"
                            placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                            value={salesRange}
                            ref={salesInputRef}
                            required
                            readOnly
                            onChange={(e) => setSalesRange(e.target.value)}
                          />
                          <span
                            className="input-group-text"
                            role="button"
                            tabIndex={0}
                            onClick={clearSales}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clearSales(); }}
                            aria-label="クリア"
                            title="クリア"
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                          >×</span>
                        </div>
                        {feedback('salesRange')}
                      </div>
                      {/* 表示順 */}
                      <div className="mb-4">
                        <div className="d-flex flex-wrap mb-2">
                          <h5 className="mb-0 text-body-highlight me-2">表示順</h5>
                        </div>
                        <input
                          className="form-control"
                          type="number"
                          name="order"
                          value={displayOrder ?? ''}
                          onChange={(e) => setDisplayOrder(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* 画像ブロック（単枚アップロード） */}
                <div className="col-12 col-sm-6 col-xl-12">
                  <div className="card">
                    <div className="card-body">
                      <h4 className="card-title mb-3">クーポン画像</h4>

                      {!imagePreview && (
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={handlePickClick}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePickClick(); }}
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                          onDragEnter={handleDragOver}
                          onDragLeave={handleDragLeave}
                          className={`border border-dashed rounded-3 p-4 text-center ${isDragOver ? 'bg-body-tertiary' : ''}`}
                          style={{ cursor: 'pointer' }}
                          aria-label="画像をドラッグ&ドロップ、またはクリックして選択"
                        >
                          <p className="mb-2">ここに画像をドラッグ＆ドロップ</p>
                          <p className="mb-2">または</p>
                          <button type="button" className="btn btn-outline-primary" disabled={savingOrUploading}>ファイルを選択</button>
                          <p className="text-body-tertiary mt-2 mb-0">対応形式: 画像ファイル（1枚まで）</p>
                        </div>
                      )}

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        disabled={savingOrUploading}
                      />

                      {imagePreview && (
                        <div className="d-flex align-items-start gap-3">
                          <img
                            src={imagePreview}
                            alt="プレビュー"
                            className="rounded border"
                            style={{ width: 160, height: 160, objectFit: 'cover' }}
                          />
                          <div className="flex-grow-1">
                            <div className="mb-2 small text-body-secondary">
                              {imageFile?.name} {imageFile ? `(${Math.round(imageFile.size / 1024)} KB)` : ''}
                            </div>
                            <div className="d-flex gap-2">
                              <button type="button" className="btn btn-outline-secondary" onClick={handlePickClick} disabled={savingOrUploading}>
                                画像を変更
                              </button>
                              <button type="button" className="btn btn-outline-danger" onClick={clearImage} disabled={savingOrUploading}>
                                画像を削除
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {loading && <div className="text-body-secondary mt-3">読み込み中...</div>}
                      {!loading && apiError && <div className="text-danger mt-3">{apiError}</div>}
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Right column ends */}
          </div>
        </form>
      </>
    </Layout>
  );
}
